import { cn, initials } from "@/lib/utils";

interface LeadAvatarProps {
  name: string;
  size?: number;
  className?: string;
  /** Highlight ring (for active/unread state). */
  ring?: "none" | "brand" | "subtle";
}

/**
 * Hash a string into a number deterministically. Same name → same color.
 */
function hash(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

/**
 * Curated gradient pairs that look good on a near-black background.
 * Each pair: [from, via, to] — designed not to clash with the brand cyan
 * that's reserved for Allison.
 */
const GRADIENTS: Array<[string, string, string]> = [
  ["#A78BFA", "#8B5CF6", "#6D28D9"], // purple
  ["#F472B6", "#EC4899", "#BE185D"], // pink
  ["#FB923C", "#F97316", "#C2410C"], // orange
  ["#FACC15", "#EAB308", "#A16207"], // amber
  ["#4ADE80", "#22C55E", "#15803D"], // green
  ["#2DD4BF", "#14B8A6", "#0F766E"], // teal
  ["#60A5FA", "#3B82F6", "#1D4ED8"], // blue
  ["#F87171", "#EF4444", "#B91C1C"], // red
];

export function LeadAvatar({
  name,
  size = 40,
  className,
  ring = "none",
}: LeadAvatarProps) {
  const gradient = GRADIENTS[hash(name) % GRADIENTS.length];
  const [from, via, to] = gradient;

  return (
    <span
      className={cn(
        "relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full font-semibold text-white",
        "shadow-[inset_0_1px_0_rgba(255,255,255,0.18),inset_0_-2px_4px_rgba(0,0,0,0.3)]",
        ring === "brand" &&
          "ring-2 ring-brand/50 ring-offset-2 ring-offset-background",
        ring === "subtle" &&
          "ring-1 ring-white/10 ring-offset-2 ring-offset-background",
        className
      )}
      style={{
        width: size,
        height: size,
        background: `linear-gradient(135deg, ${from} 0%, ${via} 50%, ${to} 100%)`,
        fontSize: size * 0.36,
      }}
      aria-label={name}
    >
      {/* Subtle inner highlight */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-full"
        style={{
          background:
            "radial-gradient(circle at 30% 25%, rgba(255,255,255,0.25) 0%, transparent 50%)",
        }}
      />
      <span className="relative tracking-tight">{initials(name)}</span>
    </span>
  );
}
